import {
  ISpecificationSupplierProduct,
  IProductSupplierItem,
} from "@/shared/services/interface/responses/ISupplierResponsesAllResponse";
import { TableRowDetailsItem } from "../types";

export const formatSecondsToDuration = (seconds?: number) => {
  if (!seconds || seconds <= 0) return "-";
  const minutes = Math.floor(seconds / 60);
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hrs > 0) {
    return `${hrs}sa ${mins}dk`;
  }

  if (mins > 0) {
    const remainingSeconds = Math.floor(seconds % 60);
    return `${mins}dk ${remainingSeconds}sn`;
  }

  return `${seconds}sn`;
};

export const formatRelativeTime = (dateString?: string | null) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";

  const diff = Date.now() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}g`;
  if (hours > 0) return `${hours}s`;
  if (minutes > 0) return `${minutes}d`;
  return `${Math.max(seconds, 1)}sn`;
};

export const getSupplierStatus = (
  supplier: IProductSupplierItem
): TableRowDetailsItem["status"] => {
  if (supplier.answersCount > 0 || supplier.isSurveyCompleted) {
    return "replied";
  }

  if (supplier.inviteStatus === 2) {
    return "cancelled";
  }

  if (supplier.inviteStatus === 1) {
    return "opened";
  }

  return "sent";
};

export const buildProgress = (
  current: number,
  total: number
): TableRowDetailsItem["progress"] => {
  const safeTotal = total > 0 ? total : 1;
  const percentage = Math.min(
    100,
    Math.max(0, Math.round((current / safeTotal) * 100))
  );

  return {
    current,
    total: safeTotal,
    percentage,
  };
};

export const mapProductToRows = (
  product: ISpecificationSupplierProduct
): TableRowDetailsItem[] => {
  const totalAnswers = product.totalAnswersCount || 0;
  const rows: TableRowDetailsItem[] = [];

  if (product.procrightAi) {
    rows.push({
      id: `${product.productId}-ai`,
      user: {
        name: "Procright AI",
        isAI: true,
      },
      service: {
        name: product.productName,
        description: product.productDescription || product.companyName || "",
        logo: product.logoSrc || undefined,
      },
      progress: buildProgress(product.procrightAi.answersCount, totalAnswers),
      count: product.procrightAi.answersCount,
      time1: "-",
      time2: formatRelativeTime(product.procrightAi.lastActivityAt),
      status: product.procrightAi.answersCount > 0 ? "replied" : "sent",
    });
  }

  product.suppliers.forEach((supplier) => {
    rows.push({
      id: supplier.supplierId,
      user: {
        name:
          supplier.supplierName ||
          supplier.supplierEmail ||
          supplier.supplierId,
        email: supplier.supplierEmail || undefined,
        inviteId: supplier.inviteId || undefined,
      },
      service: {
        name: product.productName,
        description: product.productDescription || product.companyName || "",
        logo: product.logoSrc || undefined,
      },
      progress: buildProgress(supplier.answersCount, totalAnswers),
      count: supplier.commentsCount,
      time1: formatSecondsToDuration(supplier.totalDurationSeconds),
      time2: formatRelativeTime(supplier.lastActivityAt),
      status: getSupplierStatus(supplier),
    });
  });

  return rows;
};

